/**
 * Splat objects as selections: a whole splat in the scene (the main scan, a Splat Edit
 * "Separate" result, a splat added with the 3D Model & Splat button) used as the area a feature
 * applies to, instead of a drawn sphere, box or outline. Focus & Highlight, haze regions and
 * annotation layers offer it through the shared shape picker (`shape: 'splats'`, `splatIds`);
 * any other per-splat effect can reuse the same machinery through
 * `RegionColorEffect.objectSets` (splat-region-effects.ts).
 *
 * HOW IT REACHES THE GPU. Selections name objects by stable id: a custom mesh's `id`, or
 * MAIN_SPLAT_OBJECT_ID for the scan. The viewer registers each splat entity under its id
 * (registerSplatObject). The region composer gives each feature lane (focus region 0..3, haze
 * region 0..3, annotation object shape 0..7, ...) one bit of a per-splat flags value. Nothing is
 * tested per splat on the CPU and nothing geometric is tested on the GPU:
 *
 * - Unified splats (the scan and every splat model share one work buffer, so they sort and blend
 *   together) carry a small per-component SLOT number in the work buffer itself: while a
 *   selection exists, every unified component gets a copy-stage part (workBufferModifiers.ts)
 *   that writes its slot (a per-component uniform, which the copy stage honours) into the two
 *   lowest mantissa bits of each centre coordinate (3 x 2 bits = slots 0..63; 0 = not an
 *   object). That moves a centre by at most three units in the last place: invisible, and no
 *   extra memory. The splat shader reads the bits back from the centre it is given and looks the
 *   slot's flags up in a 64-entry uniform table (16 vec4) on the template material.
 * - Non-unified splats (a plain swap scan with its own material) have no copy stage: they get
 *   their flags as a uniform on that material.
 *
 * Changing which objects are selected rewrites the table and never recompiles. It does re-copy
 * every unified splat once: the change reaches the region owner through `set`, which re-sets
 * `uRegionTransform` on every component (splat-region-effects.ts). Adding or removing the first /
 * last selection also installs / removes the copy-stage part. Selected components get slots
 * first; a component past MAX_SPLAT_OBJECT_SLOTS gets slot 0: it is never selected, and never
 * mistaken for another object.
 */
import * as pc from 'playcanvas';

/** The scene's main scan, and the scans Splat Swap and Compare show in its place. The editor's outliner lists it under the same id. */
export const MAIN_SPLAT_OBJECT_ID = 'main-splat';
/** Objects one selection can name. */
export const MAX_SPLAT_OBJECT_IDS = 16;
/** Unified splat components that can carry a slot (2 bits in each of the 3 centre coordinates; slot 0 = none). */
export const MAX_SPLAT_OBJECT_SLOTS = 63;
/** Lanes across all effects: the per-splat flags are a float, exact up to 2^24. */
export const SPLAT_OBJECT_FLAG_BITS = 24;
/** Template-material define that switches the shader to reading slots from the centre bits. */
export const SPLAT_OBJECT_SLOTS_DEFINE = 'SPLAT_OBJECT_SLOTS';
/** Uniforms: the slot -> flags table on the template material, and a non-unified material's own flags. */
export const SPLAT_OBJECT_TABLE_UNIFORM = 'uSplatObjectSlots';
export const SPLAT_OBJECT_FLAGS_UNIFORM = 'uSplatObjectFlags';
/** Per-component uniform the copy-stage part writes into the centre bits. */
export const SPLAT_OBJECT_SLOT_UNIFORM = 'uSplatObjectSlot';
/** Key of the copy-stage part (workBufferModifiers.ts). Parts run in key order: this one must be
 * last, because it writes the low bits of the final centre (after e.g. wind has moved it). */
export const SPLAT_OBJECT_MODIFIER_KEY = 'zzSplatObjectSlot';
const TABLE_VECTORS = (MAX_SPLAT_OBJECT_SLOTS + 1) / 4;
export const splatObjectTableUniform = (index: number): string => `${SPLAT_OBJECT_TABLE_UNIFORM}${index}`;

// ── Registry: which entity is which object ──────────────────────────────────────────────────

const objectIds = new WeakMap<pc.GraphNode, string>();
let registryRevision = 0;

/**
 * Mark `entity` (and every gsplat below it, e.g. a GLB's KHR_gaussian_splatting nodes) as the
 * splat object `id`. Null removes the mark.
 */
export function registerSplatObject(entity: pc.GraphNode, id: string | null): void {
    const current = objectIds.get(entity) ?? null;
    if (current === id) return;
    if (id) objectIds.set(entity, id);
    else objectIds.delete(entity);
    registryRevision++;
}

/** The splat object `node` belongs to: its own mark or the nearest marked ancestor's. */
export function splatObjectIdOf(node: pc.GraphNode | null | undefined): string | null {
    for (let n: pc.GraphNode | null | undefined = node; n; n = n.parent) {
        const id = objectIds.get(n);
        if (id) return id;
    }
    return null;
}

/** Bumped whenever an object is (un)registered: cached per-component flags are stale. */
export const splatObjectRegistryRevision = (): number => registryRevision;

/** A stored `splatIds` list: non-empty unique strings, at most MAX_SPLAT_OBJECT_IDS. */
export function normalizeSplatObjectIds(value: unknown): string[] {
    if (!Array.isArray(value)) return [];
    const out: string[] = [];
    for (const v of value) {
        if (typeof v !== 'string') continue;
        const id = v.trim();
        if (!id || id.length > 200 || out.includes(id)) continue;
        out.push(id);
        if (out.length >= MAX_SPLAT_OBJECT_IDS) break;
    }
    return out;
}

/** The gsplat components of registered splat objects under `root` (optionally only those of `ids`). */
export function splatObjectComponents(root: pc.Entity, ids?: ReadonlyArray<string>): Array<{ id: string; component: pc.GSplatComponent }> {
    const out: Array<{ id: string; component: pc.GSplatComponent }> = [];
    for (const component of root.findComponents('gsplat') as pc.GSplatComponent[]) {
        const id = splatObjectIdOf(component.entity);
        if (id && (!ids || ids.includes(id))) out.push({ id, component });
    }
    return out;
}

const localAabb = (component: pc.GSplatComponent): pc.BoundingBox | null =>
    (component.resource as { aabb?: pc.BoundingBox } | null)?.aabb ?? component.customAabb ?? null;

/** The eight corners of a component's local bounds through `matrix`. */
export function splatComponentCorners(component: pc.GSplatComponent, matrix: pc.Mat4): pc.Vec3[] {
    const box = localAabb(component);
    if (!box) return [];
    const min = box.getMin();
    const max = box.getMax();
    const corners: pc.Vec3[] = [];
    for (let i = 0; i < 8; i++)
        corners.push(matrix.transformPoint(new pc.Vec3(i & 1 ? max.x : min.x, i & 2 ? max.y : min.y, i & 4 ? max.z : min.z)));
    return corners;
}

/**
 * Axis-aligned box around the objects `ids` in `root`'s local space (the scene root's runtime
 * coordinates). Null when none of them is loaded.
 */
export function splatObjectSceneBounds(root: pc.Entity, ids: ReadonlyArray<string>): { min: pc.Vec3; max: pc.Vec3 } | null {
    const inverse = new pc.Mat4().copy(root.getWorldTransform()).invert();
    const min = new pc.Vec3(Infinity, Infinity, Infinity);
    const max = new pc.Vec3(-Infinity, -Infinity, -Infinity);
    for (const { component } of splatObjectComponents(root, ids)) {
        const m = new pc.Mat4().mul2(inverse, component.entity.getWorldTransform());
        for (const c of splatComponentCorners(component, m)) {
            min.min(c);
            max.max(c);
        }
    }
    return Number.isFinite(min.x) ? { min, max } : null;
}

/** Draw each object's bounds (its own box, through its transform) in world space. Editor only. */
export function drawSplatObjectBounds(app: pc.AppBase, root: pc.Entity, ids: ReadonlyArray<string>, color: pc.Color): void {
    for (const { component } of splatObjectComponents(root, ids)) {
        if (!component.entity.enabled) continue;
        const c = splatComponentCorners(component, component.entity.getWorldTransform());
        if (c.length !== 8) continue;
        for (let i = 0; i < 8; i++)
            for (const bit of [1, 2, 4]) if (!(i & bit)) app.drawLine(c[i], c[i | bit], color, false);
    }
}

// ── Lanes: which bit of a component's flags each selection set is ───────────────────────────

/** An effect's selection sets in composition order, and the first flag bit they use. */
export interface SplatObjectLaneLayout {
    key: string;
    base: number;
    sets: ReadonlyArray<ReadonlyArray<string>>;
}

/**
 * Lay out the effects' selection sets on consecutive flag bits (effects in the order given). Sets
 * past SPLAT_OBJECT_FLAG_BITS get no bit (they select nothing).
 */
export function splatObjectLaneLayout(effects: ReadonlyArray<{ key: string; objectSets?: ReadonlyArray<ReadonlyArray<string>> }>): SplatObjectLaneLayout[] {
    const out: SplatObjectLaneLayout[] = [];
    let base = 0;
    for (const effect of effects) {
        const sets = effect.objectSets;
        if (!sets?.length) continue;
        const usable = sets.slice(0, Math.max(0, SPLAT_OBJECT_FLAG_BITS - base));
        out.push({ key: effect.key, base, sets: usable });
        base += usable.length;
    }
    return out;
}

/** A component's flags: bit (base + i) is set when its object is in set i of an effect. */
export function splatObjectFlags(id: string | null, layout: ReadonlyArray<SplatObjectLaneLayout>): number {
    if (!id) return 0;
    let flags = 0;
    for (const effect of layout) effect.sets.forEach((set, i) => {
        if (set.includes(id)) flags += 2 ** (effect.base + i);
    });
    return flags;
}

/**
 * Slots (1..MAX_SPLAT_OBJECT_SLOTS) for registered unified components: selected ones (non-zero
 * flags) first, so a scene with more components than slots still slots every selected one, then
 * the rest; each group in component id order, so slots stay put while nothing changes. Components
 * left out get slot 0.
 */
export function assignSplatObjectSlots<C extends { id: number }>(objects: ReadonlyArray<{ component: C; flags: number }>): Map<C, number> {
    const ordered = [...objects].sort((a, b) => Number(!a.flags) - Number(!b.flags) || a.component.id - b.component.id);
    return new Map(ordered.slice(0, MAX_SPLAT_OBJECT_SLOTS).map((o, i) => [o.component, i + 1]));
}

/** Shader call for lane `lane` of effect `key`: 1.0 when the splat's object is in that set, else 0.0. */
export const splatObjectLaneCall = (key: string, lane: number): string => `splatObject_${key}_${lane}()`;

/** Global the composer's centre wrapper fills once per splat, before any effect runs. */
export const SPLAT_OBJECT_GLOBAL = 'gSplatObjectFlags';
/** Statement that fills it from the incoming (unmodified) centre `center`. */
export const splatObjectFlagsStatement = (lang: 'glsl' | 'wgsl'): string =>
    lang === 'glsl' ? `${SPLAT_OBJECT_GLOBAL} = splatObjectFlagsAt(center);` : `${SPLAT_OBJECT_GLOBAL} = splatObjectFlagsAt(*center);`;

const literal = (v: number): string => (Number.isInteger(v) ? `${v}.0` : String(v));

/**
 * The render-stage prelude for `layout`: reading a unified splat's slot from its centre bits and
 * its flags from the slot table (under SPLAT_OBJECT_SLOTS, set on the template material only),
 * the material's own flags otherwise; the global; one lane function per selection set. Empty
 * when nothing is selected.
 */
export function splatObjectPrelude(layout: ReadonlyArray<SplatObjectLaneLayout>, lang: 'glsl' | 'wgsl'): string {
    if (!layout.length) return '';
    const glsl = lang === 'glsl';
    const t = (i: number) => (glsl ? splatObjectTableUniform(i) : `uniform.${splatObjectTableUniform(i)}`);
    const tableDecl = Array.from({ length: TABLE_VECTORS }, (_, i) => (glsl ? `uniform vec4 ${splatObjectTableUniform(i)};` : `uniform ${splatObjectTableUniform(i)}: vec4f;`)).join('\n');
    // Pick the slot's vec4 with a select chain (no dynamic uniform indexing), then its lane.
    const chain = Array.from({ length: TABLE_VECTORS - 1 }, (_, i) => i).reduceRight(
        (rest, i) => (glsl ? `(v == ${i}u ? ${t(i)} : ${rest})` : `select(${rest}, ${t(i)}, v == ${i}u)`),
        t(TABLE_VECTORS - 1),
    );
    const lanes = layout
        .flatMap((effect) =>
            effect.sets.map((_, i) => {
                const name = splatObjectLaneCall(effect.key, i).replace('()', '');
                const power = literal(2 ** (effect.base + i));
                return glsl
                    ? `float ${name}() { return mod(floor(${SPLAT_OBJECT_GLOBAL} / ${power}), 2.0); }`
                    : `fn ${name}() -> f32 { let q = floor(${SPLAT_OBJECT_GLOBAL} / ${power}); return q - 2.0 * floor(q / 2.0); }`;
            }),
        )
        .join('\n');
    return glsl
        ? `#ifdef ${SPLAT_OBJECT_SLOTS_DEFINE}
${tableDecl}
float splatObjectFlagsAt(vec3 center) { uvec3 b = floatBitsToUint(center) & uvec3(3u); uint s = b.x | (b.y << 2u) | (b.z << 4u); uint v = s >> 2u; uint c = s & 3u; vec4 t = ${chain}; return c == 0u ? t.x : (c == 1u ? t.y : (c == 2u ? t.z : t.w)); }
#else
uniform vec4 ${SPLAT_OBJECT_FLAGS_UNIFORM};
float splatObjectFlagsAt(vec3 center) { return ${SPLAT_OBJECT_FLAGS_UNIFORM}.x; }
#endif
float ${SPLAT_OBJECT_GLOBAL} = 0.0;
${lanes}
`
        : `#ifdef ${SPLAT_OBJECT_SLOTS_DEFINE}
${tableDecl}
fn splatObjectFlagsAt(center: vec3f) -> f32 { let b = bitcast<vec3u>(center) & vec3u(3u); let s = b.x | (b.y << 2u) | (b.z << 4u); let v = s >> 2u; let c = s & 3u; let t = ${chain}; return select(select(select(t.w, t.z, c == 2u), t.y, c == 1u), t.x, c == 0u); }
#else
uniform ${SPLAT_OBJECT_FLAGS_UNIFORM}: vec4f;
fn splatObjectFlagsAt(center: vec3f) -> f32 { return uniform.${SPLAT_OBJECT_FLAGS_UNIFORM}.x; }
#endif
var<private> ${SPLAT_OBJECT_GLOBAL}: f32 = 0.0;
${lanes}
`;
}

/**
 * The copy-stage part (workBufferModifiers.ts) every unified component gets while a selection
 * exists: writes the component's slot (SPLAT_OBJECT_SLOT_UNIFORM) into the two lowest mantissa
 * bits of each centre coordinate. Zero and denormal coordinates first become the smallest normal
 * float of their sign (GPUs may flush denormals, which would drop the bits); that is ~1e-38.
 */
export const SPLAT_OBJECT_COPY_PART = {
    glsl: `uniform float ${SPLAT_OBJECT_SLOT_UNIFORM};
uint splatObjectSlotBits(uint v, uint s) { if ((v & 0x7F800000u) == 0u) { v = (v & 0x80000000u) | 0x00800000u; } return (v & 0xFFFFFFFCu) | s; }
void modifySplatCenter(inout vec3 center) { uint s = uint(${SPLAT_OBJECT_SLOT_UNIFORM} + 0.5); uvec3 b = floatBitsToUint(center); center = uintBitsToFloat(uvec3(splatObjectSlotBits(b.x, s & 3u), splatObjectSlotBits(b.y, (s >> 2u) & 3u), splatObjectSlotBits(b.z, (s >> 4u) & 3u))); }
void modifySplatRotationScale(vec3 originalCenter, vec3 modifiedCenter, inout vec4 rotation, inout vec3 scale) {}
void modifySplatColor(vec3 center, inout vec4 color) {}
`,
    wgsl: `uniform ${SPLAT_OBJECT_SLOT_UNIFORM}: f32;
fn splatObjectSlotBits(v0: u32, s: u32) -> u32 { var v = v0; if ((v & 0x7F800000u) == 0u) { v = (v & 0x80000000u) | 0x00800000u; } return (v & 0xFFFFFFFCu) | s; }
fn modifySplatCenter(center: ptr<function, vec3f>) { let s = u32(uniform.${SPLAT_OBJECT_SLOT_UNIFORM} + 0.5); let b = bitcast<vec3u>(*center); *center = bitcast<vec3f>(vec3u(splatObjectSlotBits(b.x, s & 3u), splatObjectSlotBits(b.y, (s >> 2u) & 3u), splatObjectSlotBits(b.z, (s >> 4u) & 3u))); }
fn modifySplatRotationScale(originalCenter: vec3f, modifiedCenter: vec3f, rotation: ptr<function, vec4f>, scale: ptr<function, vec3f>) {}
fn modifySplatColor(center: vec3f, color: ptr<function, vec4f>) {}
`,
};

/** CPU twin of the copy stage and the shader's read-back, for tests: centre bits -> slot. */
export function splatObjectSlotFromCenter(center: ArrayLike<number>): number {
    const f = new Float32Array(Array.from(center).slice(0, 3));
    const u = new Uint32Array(f.buffer);
    return (u[0] & 3) | ((u[1] & 3) << 2) | ((u[2] & 3) << 4);
}
/** CPU twin of the copy-stage part: `center` with `slot` written into its low bits. */
export function writeSplatObjectSlot(center: ArrayLike<number>, slot: number): Float32Array {
    const f = new Float32Array(Array.from(center).slice(0, 3));
    const u = new Uint32Array(f.buffer);
    for (let i = 0; i < 3; i++) {
        if ((u[i] & 0x7f800000) === 0) u[i] = (u[i] & 0x80000000) | 0x00800000;
        u[i] = ((u[i] & 0xfffffffc) | ((slot >> (2 * i)) & 3)) >>> 0;
    }
    return f;
}

/** Fill the slot table (slot i = flags[i]; slot 0 and missing slots select nothing). */
export function writeSplatObjectTable(target: Float32Array[], flagsBySlot: ReadonlyArray<number>): void {
    for (let i = 0; i < TABLE_VECTORS; i++) target[i].fill(0);
    flagsBySlot.forEach((flags, slot) => {
        if (slot <= 0 || slot > MAX_SPLAT_OBJECT_SLOTS) return;
        target[slot >> 2][slot & 3] = flags;
    });
}
export const createSplatObjectTable = (): Float32Array[] => Array.from({ length: TABLE_VECTORS }, () => new Float32Array(4));

// ── Viewport pick (editor) ──────────────────────────────────────────────────────────────────

/**
 * The splat object drawn under a screen point (CSS pixels on the canvas), or null: the object with
 * a splat centre within `radiusPx` of the point nearest the camera. `surfaceDepth` (camera-space
 * distance of the scan surface under the point, e.g. from a collision raycast) stands in for
 * objects without CPU-side centres (a LOD-streamed scan): the main scan wins when nothing else is
 * in front of it. CPU only; no GPU pick pass.
 */
export function pickSplatObjectAtScreen(
    root: pc.Entity,
    camera: pc.CameraComponent,
    sx: number,
    sy: number,
    options: { width: number; height: number; radiusPx?: number; surfaceDepth?: number | null },
): string | null {
    const radius = options.radiusPx ?? 6;
    const vp = new pc.Mat4().mul2(camera.projectionMatrix, camera.viewMatrix);
    const view = camera.viewMatrix;
    const { width, height } = options;
    let best: { id: string; depth: number } | null = null;
    let mainWithoutCentres = false;
    for (const { id, component } of splatObjectComponents(root)) {
        if (!component.entity.enabled) continue;
        const centers = (component.resource as { centers?: Float32Array | null } | null)?.centers ?? null;
        if (!centers?.length) {
            if (id === MAIN_SPLAT_OBJECT_ID) mainWithoutCentres = true;
            continue;
        }
        const world = component.entity.getWorldTransform();
        // Skip objects whose bounds are off the point (cheap reject before walking the centres).
        const corners = splatComponentCorners(component, world);
        const screen = corners.map((c) => {
            const clip = vp.transformVec4(new pc.Vec4(c.x, c.y, c.z, 1), new pc.Vec4());
            return clip.w > 1e-6 ? { x: ((clip.x / clip.w) * 0.5 + 0.5) * width, y: (1 - ((clip.y / clip.w) * 0.5 + 0.5)) * height } : null;
        });
        if (screen.length && screen.every((s) => !s)) continue;
        if (screen.length && screen.every((s) => !!s)) {
            const xs = screen.map((s) => s!.x);
            const ys = screen.map((s) => s!.y);
            if (sx < Math.min(...xs) - radius || sx > Math.max(...xs) + radius || sy < Math.min(...ys) - radius || sy > Math.max(...ys) + radius) continue;
        }
        const m = new pc.Mat4().mul2(vp, world).data;
        const mv = new pc.Mat4().mul2(view, world).data;
        for (let i = 0; i + 2 < centers.length; i += 3) {
            const x = centers[i], y = centers[i + 1], z = centers[i + 2];
            const w = m[3] * x + m[7] * y + m[11] * z + m[15];
            if (w <= 1e-6) continue;
            const px = (((m[0] * x + m[4] * y + m[8] * z + m[12]) / w) * 0.5 + 0.5) * width;
            if (Math.abs(px - sx) > radius) continue;
            const py = (1 - (((m[1] * x + m[5] * y + m[9] * z + m[13]) / w) * 0.5 + 0.5)) * height;
            if (Math.abs(py - sy) > radius) continue;
            const depth = -(mv[2] * x + mv[6] * y + mv[10] * z + mv[14]);
            if (depth > 0 && (!best || depth < best.depth)) best = { id, depth };
        }
    }
    const surface = options.surfaceDepth;
    if (mainWithoutCentres && typeof surface === 'number' && Number.isFinite(surface) && (!best || surface < best.depth))
        return MAIN_SPLAT_OBJECT_ID;
    return best?.id ?? null;
}
